import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Divider, Chip, Grid, Paper } from '@mui/material';
import { styled } from '@mui/system';
import API_URL from '../apiConfig';
import pic1 from '../assets/noorder.svg';

const StatusChip = styled(Chip)(({ theme, status }) => {
  let bgcolor = '#fcf4f4';
  let color = '#d46a89';
  if (status?.toLowerCase() === 'pending') {
    bgcolor = '#fff4e5'; color = '#ff9800';
  } else if (status?.toLowerCase() === 'delivered' || status?.toLowerCase() === 'completed') {
    bgcolor = '#edf7ed'; color = '#4caf50';
  } else if (status?.toLowerCase() === 'processing') {
    bgcolor = '#e3f2fd'; color = '#2196f3';
  } else if (status?.toLowerCase() === 'cancelled') {
    bgcolor = '#fdeded'; color = '#ef5350';
  }
  return {
    backgroundColor: bgcolor,
    color: color,
    fontWeight: 700,
    borderRadius: '12px'
  };
});

const formatPrice = (price) => {
  if (!price) return 'LKR. 0.00';
  const strPrice = String(price);
  if (strPrice.startsWith('LKR')) return strPrice;
  if (strPrice.startsWith('Rs.')) return strPrice.replace('Rs.', 'LKR.');
  const match = strPrice.replace(/,/g, '').match(/\d+(\.\d+)?/);
  const num = match ? parseFloat(match[0]) : 0;
  return `LKR. ${num.toFixed(2)}`;
};

const OrderSummary = () => {
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    fetch(`${API_URL}/api/orders`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
      .then(res => res.json())
      .then(data => {
        // If data is array
        if (Array.isArray(data)) {
          setOrders(data);
        }
      })
      .catch(err => console.error(err));
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" sx={{ fontFamily: '"Playfair Display", serif', color: '#d46a89', mb: 3, fontWeight: 700, mt: 1 }}>
        My Heavenly Orders
      </Typography>

      {orders.length === 0 ? (
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', background: '#fffafb', borderRadius: '24px', border: '2px dashed #fcf4f4' }}>
          <img src={pic1} alt="No Orders" style={{ width: '200px', opacity: 0.7, marginBottom: '20px' }} />
          <Typography sx={{ color: '#8c7077', fontSize: '1.2rem', fontWeight: 500 }}>
            You haven't ordered any delicious treats yet!
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {orders.map((order) => (
            <Grid item xs={12} key={order._id}>
              <Card elevation={0} sx={{ borderRadius: '24px', border: '1px solid #fcf4f4', background: '#fffafb' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#8c7077', fontWeight: 600 }}>ORDER #{order._id.substring(order._id.length - 6).toUpperCase()}</Typography>
                      <Typography variant="body2" sx={{ color: '#5c434a' }}>{new Date(order.orderPlacedTime).toLocaleDateString()}</Typography>
                    </Box>
                    <StatusChip label={order.status || 'Pending'} status={order.status} />
                  </Box>

                  <Divider sx={{ borderColor: '#fcf4f4', my: 2 }} />

                  <Box display="flex" flexDirection="column" gap={1.5}>
                    {order.items.map((product, index) => (
                      <Box key={index} display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            component="img"
                            src={product.image || 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?auto=format&fit=crop&q=80&w=100'}
                            sx={{ width: 48, height: 48, borderRadius: '12px', objectFit: 'cover', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                          />
                          <Box>
                            <Typography sx={{ color: '#5c434a', fontWeight: 700 }}>{product.name}</Typography>
                            <Typography sx={{ color: '#8c7077', fontSize: '0.85rem' }}>Qty: {product.quantity} • {product.category}</Typography>
                          </Box>
                        </Box>
                        <Typography sx={{ color: '#d46a89', fontWeight: 700 }}>{formatPrice(product.price)}</Typography>
                      </Box>
                    ))}
                  </Box>

                  <Divider sx={{ borderColor: '#fcf4f4', my: 2 }} />

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ color: '#8c7077', fontSize: '0.9rem' }}>Delivering to: <span style={{ color: '#5c434a', fontWeight: 500 }}>{order.deliveryAddress}</span></Typography>
                    <Typography sx={{ color: '#d46a89', fontWeight: 800, fontSize: '1.2rem' }}>
                      Total: {formatPrice(order.totalPrice || order.items.reduce((acc, item) => {
                        const strPrice = String(item.price);
                        const match = strPrice.replace(/,/g, '').match(/\d+(\.\d+)?/);
                        const num = match ? parseFloat(match[0]) : 0;
                        return acc + (num * (item.quantity || 1));
                      }, (order.deliveryAddress && order.deliveryAddress.includes('COD')) ? 500 : 0))}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default OrderSummary;